// application/useCapacitaciones.js  (lista por ciclo + asistentes + localidades + eliminar). Sin JSX, sin Swal.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { capacitacionesRepository } from '../infrastructure/capacitaciones.repository';
import { catalogosRepository } from '../../shared/infrastructure/catalogos.repository';
import { useCatalogo } from '../../shared/application/useCatalogo';
import { municipiosDistintos, sortByRecientes, withNombres } from '../domain/capacitacion.rules';

const EMPTY = { items: [], asistentes: [], localidades: [], status: 'idle', error: null };

// `repository` y `catalogos` son la costura para tests/mocks futuros.
export const useCapacitaciones = ({ repository = capacitacionesRepository, catalogos = catalogosRepository } = {}) => {
  const [state, setState] = useState(EMPTY);
  // null = aún no se conoce el ciclo: lo informa `SelectCiclo` (legacy) al montarse; hasta entonces no se pide nada.
  const [idCiclo, setIdCiclo] = useState(null);
  const mounted = useRef(true);
  const requestId = useRef(0);

  const actividades = useCatalogo(() => catalogos.actividades(), [catalogos]);
  const municipios = useCatalogo(() => catalogos.municipios(), [catalogos]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reload = useCallback(async () => {
    if (idCiclo === null || idCiclo === undefined) return;
    const id = ++requestId.current;
    setState(prev => ({ ...prev, status: 'loading', error: null }));
    try {
      const [items, asistentes] = await Promise.all([repository.list({ idCiclo }), catalogos.asistentes()]);
      // Localidades solo de los municipios presentes (se cachean por municipio en el repositorio).
      const porMunicipio = await Promise.all(municipiosDistintos(items).map(idMunicipio => catalogos.localidades(idMunicipio)));
      const localidades = porMunicipio.reduce((all, list) => all.concat(list), []);
      if (mounted.current && id === requestId.current) setState({ items, asistentes, localidades, status: 'success', error: null });
    } catch (error) {
      if (mounted.current && id === requestId.current) setState({ ...EMPTY, status: 'error', error });
    }
  }, [repository, catalogos, idCiclo]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Estado derivado: nada de guardar una segunda copia de la lista.
  const data = useMemo(
    () =>
      sortByRecientes(
        withNombres(state.items, {
          actividades: actividades.data,
          municipios: municipios.data,
          localidades: state.localidades,
          asistentes: state.asistentes,
        })
      ),
    [state.items, state.localidades, state.asistentes, actividades.data, municipios.data]
  );

  // Lanza el error para que la UI lo muestre (Swal) y decida qué hacer.
  const remove = useCallback(
    async uuid => {
      await repository.remove(uuid);
      await reload();
    },
    [repository, reload]
  );

  return { data, status: state.status, error: state.error, idCiclo, setIdCiclo, reload, remove };
};

export default useCapacitaciones;
